import { ReactNode, useState } from "react";
import { cn } from "@/lib/utils";
import {
  Search,
  LayoutList,
  LayoutGrid,
  Filter,
  X,
  ChevronDown,
  Columns3,
  SlidersHorizontal,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export type ViewMode = "table" | "cards";

interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

interface FilterConfig {
  id: string;
  label: string;
  options: FilterOption[];
  value: string;
  onChange: (value: string) => void;
  defaultValue: string;
}

interface GridToolbarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  filters?: FilterConfig[];
  quickFilters?: {
    options: FilterOption[];
    value: string;
    onChange: (value: string) => void;
  };
  actions?: ReactNode;
  primaryAction?: ReactNode;
  totalCount?: number;
  filteredCount?: number;
  /** "compact": one 32px row for the entity list pages. */
  density?: "comfortable" | "compact";
  /** Rendered in the row after the search (usually ChartChips). */
  chips?: ReactNode;
}

export function GridToolbar({
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search...",
  viewMode,
  onViewModeChange,
  filters,
  quickFilters,
  actions,
  primaryAction,
  totalCount,
  filteredCount,
  density = "comfortable",
  chips,
}: GridToolbarProps) {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const compact = density === "compact";

  const activeFilterCount = filters?.filter(
    (f) => f.value !== f.defaultValue
  ).length ?? 0;

  const clearAllFilters = () => {
    filters?.forEach((f) => f.onChange(f.defaultValue));
  };

  return (
    <div className={compact ? "" : "space-y-3"}>
      <div className={cn("flex flex-wrap items-center", compact ? "gap-2" : "gap-3")}>
        {viewMode !== undefined && onViewModeChange && (
          <div className={cn("flex items-center bg-muted/50 rounded-lg border border-border/50", compact ? "p-0.5" : "p-1")}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onViewModeChange("table")}
              className={cn(
                compact ? "h-7 min-h-0" : "h-8",
                "px-3 gap-2 rounded-md transition-all",
                viewMode === "table"
                  ? "bg-background shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
              data-testid="view-mode-table"
            >
              <LayoutList className="w-4 h-4" />
              <span className="text-xs font-medium">Table</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onViewModeChange("cards")}
              className={cn(
                compact ? "h-7 min-h-0" : "h-8",
                "px-3 gap-2 rounded-md transition-all",
                viewMode === "cards"
                  ? "bg-background shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
              data-testid="view-mode-cards"
            >
              <LayoutGrid className="w-4 h-4" />
              <span className="text-xs font-medium">Gallery</span>
            </Button>
          </div>
        )}

        <div className={compact ? "relative w-56 shrink-0" : "relative flex-1 min-w-[180px] basis-[220px]"}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="search"
            aria-label={searchPlaceholder}
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            className={cn("pl-10 bg-muted/30 border-border/50 focus:border-primary focus:bg-background rounded-lg text-sm", compact ? "h-8" : "h-9")}
            data-testid="grid-search"
          />
        </div>

        {chips && <div className="grow shrink basis-[max-content] min-w-0">{chips}</div>}

        {filters && filters.length > 0 && (
          <Popover open={filtersOpen} onOpenChange={setFiltersOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  compact ? "h-8" : "h-9",
                  "gap-2 rounded-lg border-border/50",
                  activeFilterCount > 0 && "border-primary text-primary-text"
                )}
                data-testid="open-filters"
              >
                <SlidersHorizontal className="w-4 h-4" />
                <span className="text-xs font-medium">Filters</span>
                {activeFilterCount > 0 && (
                  <Badge className="h-5 w-5 p-0 flex items-center justify-center bg-primary text-primary-foreground text-[10px]">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-4" align="start">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-sm">Filters</h4>
                  {activeFilterCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearAllFilters}
                      className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
                    >
                      <X className="w-3 h-3 mr-1" />
                      Clear all
                    </Button>
                  )}
                </div>
                <div className="space-y-3">
                  {filters.map((filter) => (
                    <div key={filter.id} className="space-y-1.5">
                      <label className="text-xs font-medium text-muted-foreground">
                        {filter.label}
                      </label>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className={cn(
                              "w-full justify-between h-9 text-sm",
                              filter.value !== filter.defaultValue &&
                                "border-primary text-primary-text"
                            )}
                          >
                            {filter.options.find((o) => o.value === filter.value)?.label ||
                              filter.value}
                            <ChevronDown className="w-4 h-4 opacity-50" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-56">
                          {filter.options.map((option) => (
                            <DropdownMenuCheckboxItem
                              key={option.value}
                              checked={filter.value === option.value}
                              onCheckedChange={() => filter.onChange(option.value)}
                            >
                              <span className="flex-1">{option.label}</span>
                              {option.count !== undefined && (
                                <span className="text-xs text-muted-foreground ml-2">
                                  {option.count}
                                </span>
                              )}
                            </DropdownMenuCheckboxItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ))}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        )}

        <div className="flex flex-wrap items-center gap-2 ml-auto">
          {actions && (primaryAction ? <Popover><PopoverTrigger asChild><Button variant="outline" size="sm" className={compact ? "h-8" : "h-9"}>Import & export <ChevronDown className="ml-1 h-4 w-4" /></Button></PopoverTrigger><PopoverContent align="end" className="w-56 flex flex-col gap-2">{actions}</PopoverContent></Popover> : <div className="flex flex-wrap items-center gap-2">{actions}</div>)}
          {primaryAction}
        </div>
      </div>

      {quickFilters && (
        <div className="flex items-center gap-2 flex-wrap">
          {quickFilters.options.map((option) => (
            <Button
              key={option.value}
              variant={quickFilters.value === option.value ? "default" : "outline"}
              size="sm"
              onClick={() => quickFilters.onChange(option.value)}
              className={cn(
                "h-8 px-4 rounded-full text-xs font-medium transition-all",
                quickFilters.value === option.value
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "border-border/50 hover:border-border hover:bg-muted/50"
              )}
              data-testid={`quick-filter-${option.value.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {option.label}
              {option.count !== undefined && (
                <span
                  className={cn(
                    "ml-1.5 tabular-nums",
                    quickFilters.value === option.value
                      ? "opacity-80"
                      : "text-muted-foreground"
                  )}
                >
                  {option.count}
                </span>
              )}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

interface ActiveFilterChipsProps {
  filters: FilterConfig[];
}

export function ActiveFilterChips({ filters }: ActiveFilterChipsProps) {
  const activeFilters = filters.filter((f) => f.value !== f.defaultValue);

  if (activeFilters.length === 0) return null;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs text-muted-foreground">Active filters:</span>
      {activeFilters.map((filter) => (
        <Badge
          key={filter.id}
          variant="secondary"
          className="h-6 px-2 gap-1 text-xs font-normal cursor-pointer hover:bg-secondary/80"
          onClick={() => filter.onChange(filter.defaultValue)}
        >
          {filter.label}: {filter.options.find((o) => o.value === filter.value)?.label}
          <X className="w-3 h-3" />
        </Badge>
      ))}
    </div>
  );
}
